import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { getApiErrorData } from "_shared/_errors/api-error-message";
import React, { useCallback } from "react";
import { useSession } from "users/__session/session-context";
import MessageEditor, { type MessageEditorSubmission } from "messaging/_views/components/editor/message-editor";
import {
  addComment,
  postComment,
  removeComment,
} from "work/proposals/comments/_store/comments-redux-slice";
import {
  composerDraftKey,
  proposalUncommittedInputScopeKey,
  readComposerDraft,
  writeComposerDraft,
} from "work/proposals/_store/proposal-uncommitted-input-store";
import Comment from "work/proposals/comments/comment";
import { canAddressCounterparty, onlyReviews } from "work/proposals/comments/_view/audience-switch";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import { proposalCommentPostButtonLabel } from "work/proposals/comments/proposal-comment-publishing";
import Proposal from "work/proposals/proposal";

/** What the composer says while it waits for nothing but the reader. */
export const commentComposerPlaceholder = "Type your comment here...";
/** Why the composer is refusing a comment while the proposal saves (#5124). */
export const commentComposerSavingPlaceholder = "Saving the proposal. You can comment again in a moment.";
/** Why a reviewer has no composer in the all-stakeholders conversation (#6623). */
export const commentComposerReviewersReadOnlyNotice = "Reviewers can read this thread but cannot post in it.";
/** Why anybody else who does not lead a team has no composer in the all-stakeholders conversation. */
export const commentComposerLeaderOnlyNotice = "Only your team leader writes to all stakeholders.";

/**
 * What the author is told when a comment did not post. The bulk endpoint answers 200 and says, per
 * message, why one was not saved; that reason is what the author needs, so it is shown rather than a
 * fixed sentence that reads the same for a refusal, a conflict and an outage (#6747). With no reason
 * it says nothing, and `showErrorSnackbar` says what the failure was or that support was notified
 * (#6614).
 */
export function commentPostFailureNotice(error: unknown): string | undefined {
  const issues = getApiErrorData(error);
  if (!Array.isArray(issues)) return undefined;
  const reasons = issues.filter((issue): issue is string => typeof issue === "string" && issue.trim() !== "");
  if (reasons.length === 0) return undefined;
  return `We couldn't post your comment. ${reasons.join(" ")}`;
}

const ReadOnlyNotice = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2),
}));

type CommentComposerProps = {
  proposal: Proposal;
  /**
   * The audience the composer writes to: the pane's open tab. All stakeholders when true, the reader's
   * own team when false. The tab replaced the composer's own "External Comment" switch, so the tab a
   * reader is looking at is the conversation their comment joins.
   */
  isExternal: boolean;
  isSaving?: boolean;
  readOnly?: boolean;
  /** Called once the comment has been added optimistically, so a host can scroll to it. */
  onCommentPosted?: () => void;
};

/**
 * Writes a comment onto the negotiation's conversation, in the work agreement chat's own editor: one
 * line, formatting under it, and Post beside it. No file: a proposal's comments carry text only, and the
 * server refuses a file sent to one (#5819).
 *
 * The editor is `MessageEditor`, posting through the proposal's comment store rather than straight to
 * a forum, so a comment keeps what a comment has: it is shown at once and taken back if the server
 * refuses it, and it is kept through a remount while it is being written.
 */
export default function CommentComposer(props: Readonly<CommentComposerProps>) {
  const { proposal, isExternal } = props;
  const isSaving = props.isSaving ?? false;
  const readOnly = props.readOnly ?? false;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const isViewingAsVendor = session.context?.viewingAsVendor ?? false;

  // Only a team leader speaks for their party to the other one. Everybody else reads the all-stakeholders
  // conversation and writes in their own team's.
  const mayWriteHere = !isExternal || canAddressCounterparty(proposal, session.user?.id);

  // The in-progress comment is mirrored to the durable uncommitted-input store as it is typed (S4). This
  // pane is mounted under the page's proposal, so a render where the envelope blinks unmounts it, and
  // the editor alone would take the half-written comment with it. It is deliberately NOT Redux: a store
  // write per keystroke re-runs every subscriber on the page.
  const composerScopeKey = proposalUncommittedInputScopeKey(session);
  // `undefined` means the proposal is momentarily unidentifiable — mid-refetch, mid-viewer-scope-change —
  // not that this is a composer for something else. Re-seeding is for a composer that has lost its
  // state, never for one that still has it; see #5124's successor notes in the git history.
  const composerKey = composerDraftKey(proposal.id);
  const [composer, setComposer] = React.useState<{ key?: string; text: string; generation: number }>(() => ({
    key: composerKey,
    text: readComposerDraft(composerScopeKey, proposal.id),
    generation: 0,
  }));
  if (composerKey !== undefined && composer.key !== composerKey) {
    setComposer({
      key: composerKey,
      text: readComposerDraft(composerScopeKey, proposal.id),
      generation: composer.generation + 1,
    });
  }
  const setCommentText = useCallback(
    (text: string) => {
      setComposer((current) => ({ ...current, key: composerKey ?? current.key, text }));
      writeComposerDraft(composerScopeKey, proposal.id, text);
    },
    [composerKey, composerScopeKey, proposal.id],
  );
  /** Puts text back into the editor: the editor is not controlled, so it is remounted holding it. */
  function restoreText(text: string) {
    writeComposerDraft(composerScopeKey, proposal.id, text);
    setComposer((current) => ({ ...current, text, generation: current.generation + 1 }));
  }

  /** A comment posted while the proposal saves lands ahead of the changes the save is writing (#5124). */
  const isPostingPaused = isSaving;

  function audienceThread(): CommentThread | undefined {
    if (!proposal?.id) return;
    return CommentThread.forProposal({ proposal, isViewingAsVendor, isExternal });
  }

  async function handleSubmit({ content }: MessageEditorSubmission): Promise<boolean> {
    if (readOnly || isPostingPaused || !mayWriteHere) return false;
    const thread = audienceThread();
    if (!thread || !session.user?.id) return false;
    return postText(thread, content);
  }

  function postText(thread: CommentThread, content: string): boolean {
    if (!content.trim()) return false;
    // A proposal has one thread per audience and the server get-or-creates it, so the comment names
    // its audience and lets the server resolve the thread rather than guessing at a forum id.
    const comment = new Comment({ thread, content, senderId: session.user!.id });

    // Optimistically show the comment, then immediately persist it — published to its audience by
    // default (see proposalCommentsPublishOnPost). A post that did not land takes its optimistic row back
    // out and returns the text to the composer, rather than leaving the author looking at a comment
    // nobody received.
    dispatch(addCommentThread(comment.thread));
    dispatch(addComment(comment));
    void dispatch(postComment({ session, comment }))
      .unwrap()
      .catch((error: unknown) => {
        console.error("Failed to post comment", error);
        dispatch(removeComment(comment));
        restoreText(comment.content);
        showErrorSnackbar(error, {
          doing: "posting your comment",
          operation: "proposal.comments.post",
          message: commentPostFailureNotice(error),
        });
      });
    setCommentText("");
    props.onCommentPosted?.();
    return true;
  }

  if (readOnly) return null;

  // Somebody who reads this conversation and may not post in it is told so where the composer would be,
  // rather than handed one that refuses them.
  if (!mayWriteHere) {
    return (
      <ReadOnlyNotice variant="body2" color="text.secondary" data-testid="comment-composer-read-only-notice">
        {onlyReviews(proposal, session.user?.id) ? commentComposerReviewersReadOnlyNotice : commentComposerLeaderOnlyNotice}
      </ReadOnlyNotice>
    );
  }

  const placeholder = isPostingPaused ? commentComposerSavingPlaceholder : commentComposerPlaceholder;

  return (
    <MessageEditor
      // Remounted to hold text it did not type itself: another proposal's draft, or a refused comment.
      key={`${composer.key ?? "none"}-${composer.generation}`}
      forum={audienceThread()?.toForum({ isViewingAsVendor })}
      disabled={isPostingPaused}
      placeholder={placeholder}
      submitLabel={proposalCommentPostButtonLabel}
      allowAttachments={false}
      initialText={composer.text}
      onTextChange={setCommentText}
      onSubmit={handleSubmit}
    />
  );
}
